import React, { useEffect, useState } from 'react';
import { apiRequest, getApiUrl } from '../../config/api';
import SuccessToast from '../SuccessToast';

const NOMBRE_TIPO = {
  CALENDAR: 'Calendario',
  SHEET: 'Hoja',
};

const textoDeError = (data, respaldo) => data?.detail || data?.error || respaldo;

function GoogleResourcesPanel({ botId }) {
  const [cuenta, setCuenta] = useState(null);
  const [recursos, setRecursos] = useState(null);
  const [errorCarga, setErrorCarga] = useState(null);
  const [tipo, setTipo] = useState('CALENDAR');
  const [resourceId, setResourceId] = useState('');
  const [etiqueta, setEtiqueta] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [toastExito, setToastExito] = useState(null);
  const [toastError, setToastError] = useState(null);

  useEffect(() => {
    if (botId == null) return undefined;
    let vivo = true;
    setCuenta(null);
    setRecursos(null);
    setErrorCarga(null);

    (async () => {
      try {
        const [cuentaRes, recursosRes] = await Promise.all([
          apiRequest(getApiUrl('/google/service-account')),
          apiRequest(getApiUrl(`/bots/${botId}/google-resources`)),
        ]);
        if (!vivo) return;
        const cuentaBody = await cuentaRes.json().catch(() => ({}));
        const recursosBody = await recursosRes.json().catch(() => []);
        if (!vivo) return;
        if (!cuentaRes.ok || !recursosRes.ok) {
          setErrorCarga('No se pudieron cargar los recursos de Google');
        }
        setCuenta(cuentaRes.ok ? cuentaBody : null);
        setRecursos(recursosRes.ok && Array.isArray(recursosBody) ? recursosBody : null);
      } catch {
        if (!vivo) return;
        setErrorCarga('Error de conexión');
        setCuenta(null);
        setRecursos([]);
      }
    })();

    return () => {
      vivo = false;
    };
  }, [botId]);

  const mostrarExito = (mensaje) => {
    setToastError(null);
    setToastExito(mensaje);
  };

  const mostrarError = (mensaje) => {
    setToastExito(null);
    setToastError(mensaje);
  };

  const añadir = async (event) => {
    event.preventDefault();
    const idLimpio = resourceId.trim();
    if (botId == null || !idLimpio || guardando) return;
    setGuardando(true);
    try {
      const response = await apiRequest(getApiUrl(`/bots/${botId}/google-resources`), {
        method: 'POST',
        body: JSON.stringify({
          type: tipo,
          resourceId: idLimpio,
          label: etiqueta.trim() || null,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        mostrarError(textoDeError(data, 'No se pudo añadir'));
        return;
      }
      setRecursos((prev) => [...(prev || []), data]);
      setResourceId('');
      setEtiqueta('');
      mostrarExito(`${NOMBRE_TIPO[data.type || tipo]} añadido`);
    } catch {
      mostrarError('Error de conexión');
    } finally {
      setGuardando(false);
    }
  };

  const quitar = async (recurso) => {
    if (botId == null) return;
    try {
      const response = await apiRequest(
        getApiUrl(`/bots/${botId}/google-resources/${recurso.id}`),
        { method: 'DELETE' }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        mostrarError(textoDeError(data, 'No se pudo quitar'));
        return;
      }
      setRecursos((prev) => (prev || []).filter((item) => item.id !== recurso.id));
      mostrarExito(`${NOMBRE_TIPO[recurso.type]} eliminado`);
    } catch {
      mostrarError('Error de conexión');
    }
  };

  const cargando = cuenta === null && !errorCarga;

  return (
    <section className="config-card" aria-labelledby="google-recursos-titulo" aria-busy={cargando}>
      <SuccessToast message={toastExito} onDismiss={() => setToastExito(null)} />
      <SuccessToast message={toastError} variant="error" onDismiss={() => setToastError(null)} />
      <h3 id="google-recursos-titulo">Google</h3>
      {cargando ? <p className="config-card-hint">Cargando la cuenta de servicio…</p> : null}
      {errorCarga ? <p className="google-cuenta-aviso">{errorCarga}</p> : null}
      {cuenta?.configured ? (
        <>
          <p className="google-cuenta-email">{cuenta.email}</p>
          <p className="config-card-hint">Comparte tu calendario o tu hoja con este email</p>
        </>
      ) : null}
      {cuenta && !cuenta.configured ? (
        <p className="google-cuenta-aviso">La cuenta de servicio de Google no está configurada.</p>
      ) : null}

      {recursos == null ? null : recursos.length === 0 ? (
        <p className="google-recursos-vacio">Todavía no hay calendarios ni hojas.</p>
      ) : (
        <ul className="google-recursos-lista">
          {recursos.map((recurso) => (
            <li key={recurso.id}>
              <span className="google-recurso-tipo">{NOMBRE_TIPO[recurso.type] || recurso.type}</span>
              <span className="google-recurso-texto">
                {recurso.label ? <strong>{recurso.label}</strong> : null}
                <code>{recurso.resourceId}</code>
              </span>
              <button
                type="button"
                className="admin-btn secondary"
                onClick={() => quitar(recurso)}
                aria-label={`Quitar ${recurso.label || recurso.resourceId}`}
              >
                Quitar
              </button>
            </li>
          ))}
        </ul>
      )}

      <form className="google-recurso-form" onSubmit={añadir}>
        <div className="form-group">
          <label htmlFor="google-tipo">Tipo</label>
          <select
            id="google-tipo"
            className="bm-select"
            value={tipo}
            onChange={(event) => setTipo(event.target.value)}
          >
            <option value="CALENDAR">Calendario</option>
            <option value="SHEET">Hoja</option>
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="google-resource-id">ID del recurso</label>
          <input
            id="google-resource-id"
            type="text"
            value={resourceId}
            maxLength={255}
            autoComplete="off"
            onChange={(event) => setResourceId(event.target.value)}
          />
        </div>
        <div className="form-group">
          <label htmlFor="google-label">Etiqueta</label>
          <input
            id="google-label"
            type="text"
            value={etiqueta}
            maxLength={100}
            onChange={(event) => setEtiqueta(event.target.value)}
          />
        </div>
        <button
          type="submit"
          className="admin-btn primary"
          disabled={guardando || !resourceId.trim()}
        >
          {guardando ? 'Añadiendo…' : 'Añadir'}
        </button>
      </form>
    </section>
  );
}

export default GoogleResourcesPanel;
